import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LineChart, MessageSquare } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useChat } from "../contexts/ChatContext";
import { useLanguage } from "../contexts/LanguageContext";
import { analyticsApi } from "../services/analyticsApi";
import { csatListShown, sessionsShown } from "../utils/analyticsCapabilities";
import { browserTimeZone, periodSince, presetPeriod, previousIsComplete, todayIn } from "../utils/analyticsPeriod";
import { apiErrorMessage } from "../utils/apiErrorMessage";
import { CONVERSATIONS, MANAGER } from "../utils/roles";
import CustomSelect from "./CustomSelect";
import { DownloadPeriodContext } from "./charts/downloadPeriod";
import { LOCALES } from "./charts/format";
import AdoptionTab from "./analytics/AdoptionTab";
import AnalyticsFilters from "./analytics/AnalyticsFilters";
import AssistantTab from "./analytics/AssistantTab";
import ContentTab from "./analytics/ContentTab";
import ConversationsTab from "./analytics/ConversationsTab";
import OverviewTab from "./analytics/OverviewTab";
import PageTabs, { TABS } from "./analytics/PageTabs";
import TechnicalSection from "./analytics/TechnicalSection";
import { capabilitiesOf } from "../utils/analyticsCapabilities";

// A pagina de analytics de um chat (spec do redesign do RPAA-286): o titulo com o chat, uma barra fixa
// com os filtros e os separadores, e o separador escolhido, que fica no URL (`?tab=`). Pede o /summary
// por chat e periodo, o /first-questions uma vez por mudanca, depois de o resumo chegar (plano, D6), e o
// /assistant ao abrir o separador Assistente, o /signed-in-customers ao abrir as Conversas e o /pages ao
// abrir a Adocao (as tres rotas pedidas por separador). A camada
// tecnica e o separador Tecnica so existem quando a resposta a traz; as ligacoes para conversas e o CSV
// delas so para quem as pode abrir, e o menu Exportar some num chat que nao e kodly_direct.
const notice = { borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)", color: "var(--color-text-secondary)" };

// O pedido a que uns dados respondem. Uma resposta so se desenha enquanto a chave bate com o chat e o
// periodo atuais.
const requestKeyOf = (chatId, period) => [chatId, period.from, period.to, period.tz].join("|");

const Freshness = ({ freshness }) => {
  const { translate, language } = useLanguage();
  const locale = LOCALES[language] || language;
  const when = (iso) => new Date(iso).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" });
  const sentence = (iso, updated, notUpdated) => (iso ? translate(updated, { date: when(iso) }) : translate(notUpdated));
  return (
    <footer className="text-xs space-y-1 mt-8" style={{ color: "var(--color-text-muted)" }}>
      <p>{sentence(freshness.counters_at, "analyticsCountersUpdated", "analyticsCountersNotUpdated")}</p>
      <p>{sentence(freshness.conversations_at, "analyticsConversationsUpdated", "analyticsConversationsNotUpdated")}</p>
      <p>{translate("analyticsPrecision")}</p>
    </footer>
  );
};

// O esqueleto da forma final, em vez de "A carregar". O `relative` segura o sr-only dentro do <main>.
const Skeleton = () => {
  const { translate } = useLanguage();
  const block = (key, className) => <div key={key} className={`rounded-xl animate-pulse ${className}`} style={{ backgroundColor: "var(--color-surface-raised)" }} />;
  return (
    <div aria-busy="true" className="relative space-y-6">
      <span className="sr-only">{translate("loading")}</span>
      {block("numbers", "h-40")}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{[0, 1, 2, 3].map((index) => block(index, "h-28"))}</div>
      {block("chart", "h-72")}
    </div>
  );
};

const RetryNotice = ({ error, onRetry }) => {
  const { translate } = useLanguage();
  return (
    <div role="alert" className="text-sm space-y-2" style={{ color: "var(--color-error-text)" }}>
      <p>{apiErrorMessage(error, translate("analyticsLoadFailed"))}</p>
      <button type="button" onClick={onRetry} className="px-3 py-1.5 text-sm rounded-lg border" style={{ ...notice, color: "var(--color-text-primary)" }}>
        {translate("analyticsRetry")}
      </button>
    </div>
  );
};

const AnalyticsPage = () => {
  const { translate, language } = useLanguage();
  const { canSeeSection, isPlatformOnlySuperAdmin } = useAuth();
  const { chats, selectedChatId, setSelectedChatId, loading: chatsLoading, chatsLoaded, loadChats } = useChat();
  const [searchParams, setSearchParams] = useSearchParams();
  const timeZone = useMemo(() => browserTimeZone(), []);
  const today = todayIn(timeZone);
  const [range, setRange] = useState({ preset: 30, custom: null });
  const [reload, setReload] = useState(0);
  const [data, setData] = useState(null);
  const [latest, setLatest] = useState(null);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [questionsError, setQuestionsError] = useState(null);
  const [questionsReload, setQuestionsReload] = useState(0);
  const [byOperator, setByOperator] = useState(null);
  const [byOperatorError, setByOperatorError] = useState(null);
  const [byOperatorReload, setByOperatorReload] = useState(0);
  const [activityAnswer, setActivityAnswer] = useState(null);
  const [activityFailure, setActivityFailure] = useState(null);
  const [activityReload, setActivityReload] = useState(0);
  const askedFor = useRef(null);
  const [customersAnswer, setCustomersAnswer] = useState(null);
  const [customersFailure, setCustomersFailure] = useState(null);
  const [customersReload, setCustomersReload] = useState(0);
  const customersAskedFor = useRef(null);
  const [pagesAnswer, setPagesAnswer] = useState(null);
  const [pagesFailure, setPagesFailure] = useState(null);
  const [pagesReload, setPagesReload] = useState(0);
  const pagesAskedFor = useRef(null);
  const barRef = useRef(null);
  const panelRef = useRef(null);
  const canOpenConversations = canSeeSection(CONVERSATIONS) || canSeeSection(MANAGER);
  const period = useMemo(() => ({ ...(range.custom || presetPeriod(range.preset, timeZone)), tz: timeZone }), [range, timeZone]);

  useEffect(() => {
    if (!isPlatformOnlySuperAdmin() && !chatsLoaded && !chatsLoading) loadChats();
  }, [isPlatformOnlySuperAdmin, chatsLoaded, chatsLoading, loadChats]);

  useEffect(() => {
    setData(null);
    setError(null);
    if (!selectedChatId) return undefined;
    let stale = false;
    const key = requestKeyOf(selectedChatId, period);
    analyticsApi
      .summary(selectedChatId, period)
      .then((summary) => {
        if (stale) return;
        setData({ key, summary });
        setLatest({ chatId: selectedChatId, summary });
      })
      .catch((err) => {
        if (!stale) setError(err);
      });
    return () => {
      stale = true;
    };
  }, [selectedChatId, period, reload]);

  const current = data?.key === requestKeyOf(selectedChatId, period) ? data : null;
  const summary = current?.summary;
  // O ultimo resumo deste chat, de qualquer periodo. O que nele nao depende do periodo (a camada tecnica e
  // o primeiro dia com dados) segura a barra enquanto o resumo de um periodo novo vem a caminho: o
  // `?tab=technical` nao passa pela visao geral, e o texto da comparacao (as datas, ou o "sem comparacao")
  // nao salta de um para o outro a meio. So a hora do corte de um periodo que acaba hoje espera pelo resumo
  // novo ("Comparado com ontem" e depois "... ate as 17:30", D12 do plano do Hoje): e a do resumo, e nao a
  // do relogio.
  const chatSummary = latest?.chatId === selectedChatId ? latest.summary : null;
  const sinceDay = chatSummary?.data_since ? todayIn(timeZone, new Date(chatSummary.data_since)) : null;
  const noData = Boolean(summary) && (!sinceDay || period.to < sinceDay);
  // Calcula-se aqui uma vez, para a barra e para todas as variacoes contra o anterior. Sem o primeiro dia
  // com dados (o primeiro resumo do chat ainda a caminho) a barra diz as datas com que compara.
  const previousComplete = !sinceDay || previousIsComplete(period, sinceDay);

  // As sugestoes e as perguntas: uma vez por mudanca de filtro, so com o resumo novo na mao, e nunca
  // para um periodo sem dados, onde nao se mostram.
  useEffect(() => {
    setQuestions(null);
    setQuestionsError(null);
    if (!current || !current.summary.chat.kodly_direct || noData) return undefined;
    let stale = false;
    analyticsApi
      .firstQuestions(selectedChatId, period)
      .then((first) => {
        if (!stale) setQuestions({ key: current.key, first });
      })
      .catch((err) => {
        if (!stale) setQuestionsError(err);
      });
    return () => {
      stale = true;
    };
  }, [current, noData, selectedChatId, period, questionsReload]);

  // O CSAT por operador (D13 do plano do CSAT): pede-se quando o resumo chega, como as perguntas e sem
  // esperar pelo separador Conversas, so a quem pode abrir conversas e num chat com a avaliacao e operadores.
  const wantsByOperator = Boolean(current && csatListShown(current.summary, canOpenConversations) && current.summary.chat.kodly_direct && !noData);
  useEffect(() => {
    setByOperator(null);
    setByOperatorError(null);
    if (!wantsByOperator) return undefined;
    let stale = false;
    analyticsApi
      .csatByOperator(selectedChatId, period)
      .then((answer) => {
        if (!stale) setByOperator({ key: current.key, answer });
      })
      .catch((err) => {
        if (!stale) setByOperatorError(err);
      });
    return () => {
      stale = true;
    };
  }, [current, wantsByOperator, selectedChatId, period, byOperatorReload]);
  const currentByOperator = current && byOperator?.key === current.key ? byOperator.answer : null;

  // Como o resumo: a primeira pagina so se desenha enquanto responde ao pedido atual.
  const currentQuestions = current && questions?.key === current.key ? questions : null;

  // O Assistente fica sempre num chat kodly_direct (D11 do plano do "o que o assistente fez"): sem cartoes,
  // campanhas nem encomendas ainda tem as ferramentas que o agente usou, e isso e conteudo em qualquer chat
  // com um agente. So a Tecnica depende do ultimo resumo do chat, para a fila nao saltar enquanto carrega o
  // de um periodo novo.
  const tabs = TABS.filter((tab) => tab !== "technical" || chatSummary?.technical);
  const requested = searchParams.get("tab");
  const tab = tabs.includes(requested) ? requested : "overview";
  // So muda o `tab`: o resto da query fica, e cada escolha e um passo no historico do browser.
  const chooseTab = (next) =>
    setSearchParams((params) => {
      const updated = new URLSearchParams(params);
      if (next === "overview") updated.delete("tab");
      else updated.set("tab", next);
      return updated;
    });
  // A faixa dos separadores fica enquanto um filtro carrega. Sai quando o resumo falha (o erro e o unico
  // conteudo, e nao ha painel por baixo) e quando o resumo diz que nao ha nada.
  const showTabs = Boolean(selectedChatId) && !error && !(summary && (!summary.chat.kodly_direct || noData));

  // O que cada papel pode exportar (entrega 5 do redesign): as conversas so quem as pode abrir, as contagens
  // e as sugestoes quem ve a pagina. Num chat que nao e kodly_direct nao ha nada a exportar, e isso diz-o
  // tambem o ultimo resumo do chat enquanto o de um periodo novo carrega. Antes de qualquer resumo o menu fica.
  const exportOptions = chatSummary?.chat.kodly_direct === false ? [] : [...(canOpenConversations ? ["conversations"] : []), "daily", "firstQuestions"];

  // O que o assistente fez (D12): pede-se ao abrir o separador Assistente, com o resumo do pedido atual na
  // mao e de um chat com dados. `askedFor` guarda o resumo (o objeto `current`) a que se pediu: voltar ao
  // separador nao pede outra vez porque o resumo e o mesmo, mas um periodo novo, um chat novo e escolher
  // outra vez o mesmo periodo (chega um resumo novo) pedem, como o resumo e as primeiras perguntas. Uma
  // resposta ou uma falha de um resumo antigo nao se guarda, e um erro liberta o pedido para o "Tentar de
  // novo". Uma rota que falha (um backend sem ela) so fica no estado: nada rebenta.
  const wantsActivity = tab === "assistant" && Boolean(current) && current.summary.chat.kodly_direct && !noData;
  useEffect(() => {
    if (!wantsActivity || askedFor.current === current) return;
    askedFor.current = current;
    setActivityFailure(null);
    analyticsApi
      .assistant(selectedChatId, period)
      .then((answer) => {
        if (askedFor.current === current) setActivityAnswer({ for: current, answer });
      })
      .catch((err) => {
        if (askedFor.current !== current) return;
        askedFor.current = null;
        setActivityFailure({ for: current, error: err });
      });
  }, [wantsActivity, current, selectedChatId, period, activityReload]);
  const activity = current && activityAnswer?.for === current ? activityAnswer.answer : null;
  const activityError = current && activityFailure?.for === current ? activityFailure.error : null;

  // Os clientes com sessao iniciada (D9 do item 10): pelo mesmo padrao, ao abrir o separador Conversas, com a
  // regra que desenha a seccao (`sessionsShown`). Um backend de antes da leitura nao tem a seccao nem a rota,
  // e o pedido nao sai.
  const wantsCustomers = tab === "conversations" && Boolean(current) && current.summary.chat.kodly_direct && !noData && sessionsShown(current.summary);
  useEffect(() => {
    if (!wantsCustomers || customersAskedFor.current === current) return;
    customersAskedFor.current = current;
    setCustomersFailure(null);
    analyticsApi
      .signedInCustomers(selectedChatId, period)
      .then((answer) => {
        if (customersAskedFor.current === current) setCustomersAnswer({ for: current, answer });
      })
      .catch((err) => {
        if (customersAskedFor.current !== current) return;
        customersAskedFor.current = null;
        setCustomersFailure({ for: current, error: err });
      });
  }, [wantsCustomers, current, selectedChatId, period, customersReload]);
  const customers = current && customersAnswer?.for === current ? customersAnswer.answer : null;
  const customersError = current && customersFailure?.for === current ? customersFailure.error : null;

  // As paginas com mais conversas (D8): pelo mesmo padrao, ao abrir o separador Adocao. Uma resposta ou uma
  // falha de um resumo antigo nao se guarda, e um erro liberta o pedido para o "Tentar de novo".
  const wantsPages = tab === "adoption" && Boolean(current) && current.summary.chat.kodly_direct && !noData;
  useEffect(() => {
    if (!wantsPages || pagesAskedFor.current === current) return;
    pagesAskedFor.current = current;
    setPagesFailure(null);
    analyticsApi
      .pages(selectedChatId, period)
      .then((answer) => {
        if (pagesAskedFor.current === current) setPagesAnswer({ for: current, answer });
      })
      .catch((err) => {
        if (pagesAskedFor.current !== current) return;
        pagesAskedFor.current = null;
        setPagesFailure({ for: current, error: err });
      });
  }, [wantsPages, current, selectedChatId, period, pagesReload]);
  const pages = current && pagesAnswer?.for === current ? pagesAnswer.answer : null;
  const pagesError = current && pagesFailure?.for === current ? pagesFailure.error : null;

  // Os destaques levam onde se age, com o topo do sitio logo abaixo da barra fixa, medida no clique: no
  // telemovel os filtros partem-se em mais linhas e a barra cresce. Os da visao geral levam a evolucao, mais
  // abaixo no mesmo separador. Os outros mudam de separador e o foco passa ao painel, porque o cartao
  // clicado desaparece com a troca e o foco caia no <body>.
  const scrollBelowBar = (element, behavior) => {
    element.style.scrollMarginTop = `${barRef.current.getBoundingClientRect().height}px`;
    element.scrollIntoView({ behavior, block: "start" });
  };
  const goTo = (target) => {
    if (target === "overview") {
      scrollBelowBar(document.getElementById("analytics-evolution"), "smooth");
      return;
    }
    chooseTab(target);
    scrollBelowBar(panelRef.current, "auto");
    panelRef.current.focus({ preventScroll: true });
  };

  const showSince = () => setRange({ preset: null, custom: periodSince(sinceDay, today) });
  const sinceText = sinceDay ? new Date(`${sinceDay}T00:00:00`).toLocaleDateString(LOCALES[language] || language, { dateStyle: "medium" }) : null;

  return (
    <div className="page-enter space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold flex items-center gap-2" style={{ color: "var(--color-text-primary)" }}>
          <LineChart className="w-6 h-6" aria-hidden="true" />
          {translate("analytics")}
        </h1>
        {chats.length > 1 && (
          <CustomSelect
            value={selectedChatId || ""}
            onChange={setSelectedChatId}
            label={translate("selectChat") || "Select Chat"}
            icon={MessageSquare}
            className="w-56"
            options={chats.map((chat) => ({ value: chat.id, label: chat.name }))}
          />
        )}
      </div>
      {/* O <main> rola com p-6, e um sticky para na margem de dentro dele: com top-0 ficava uma faixa de
          24px por cima da barra onde o conteudo passava a vista. O -top-6 cola a barra a borda do <main>.
          A barra fica abaixo do cabecalho (z-index 10 num item flex, que faz dele um empilhamento proprio
          e leva consigo o menu da lingua) e da camada do menu lateral no telemovel (z-30). O que abre
          dentro do conteudo (notas, tooltips, o popover das conversas) fica abaixo dela, a 4. */}
      <div ref={barRef} data-sticky-bar className="sticky -top-6 z-[5] -mx-6 px-6 pt-2 space-y-2 border-b" style={{ backgroundColor: "var(--color-bg)", borderColor: "var(--color-border)" }}>
        <AnalyticsFilters
          chatId={selectedChatId}
          range={range}
          onRange={setRange}
          period={period}
          dataSince={chatSummary?.data_since}
          previousComplete={previousComplete}
          exportOptions={exportOptions}
          cut={current ? summary.cut : null}
        />
        {showTabs ? <PageTabs tabs={tabs} active={tab} onChoose={chooseTab} /> : <div className="h-2" />}
      </div>
      {error && <RetryNotice error={error} onRetry={() => setReload((count) => count + 1)} />}
      {chatsLoaded && chats.length === 0 && (
        <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
          {translate("noChatsAvailable")}
        </p>
      )}
      {!current && !error && selectedChatId && <Skeleton />}
      {summary && !summary.chat.kodly_direct && (
        <p className="rounded-xl border p-4 text-sm" style={notice}>
          {translate("analyticsKodlyDirectOnly")}
        </p>
      )}
      {summary && summary.chat.kodly_direct && noData && (
        <div className="rounded-xl border p-4 text-sm space-y-2" style={notice}>
          <p>{sinceDay ? translate("analyticsNoDataBefore", { date: sinceText }) : translate("analyticsNoDataYet")}</p>
          {sinceDay && (
            <button type="button" onClick={showSince} className="px-3 py-1.5 rounded-lg border" style={{ ...notice, color: "var(--color-text-primary)" }}>
              {translate("analyticsShowSince", { date: sinceText })}
            </button>
          )}
        </div>
      )}
      {summary && summary.chat.kodly_direct && !noData && (
        <DownloadPeriodContext.Provider value={`${period.from}-${period.to}`}>
          <div ref={panelRef} role="tabpanel" id="analytics-panel" aria-labelledby={`analytics-tab-${tab}`} tabIndex={0}>
            {tab === "overview" && (
              <OverviewTab
                summary={summary}
                firstQuestions={currentQuestions?.first || null}
                period={period}
                today={today}
                previousComplete={previousComplete}
                onGo={goTo}
              />
            )}
            {tab === "adoption" && (
              <AdoptionTab
                summary={summary}
                period={period}
                today={today}
                previousComplete={previousComplete}
                pages={pages}
                pagesError={pagesError}
                onRetryPages={() => setPagesReload((count) => count + 1)}
              />
            )}
            {tab === "conversations" && (
              <ConversationsTab
                summary={summary}
                period={period}
                today={today}
                previousComplete={previousComplete}
                canOpenConversations={canOpenConversations}
                byOperator={currentByOperator}
                byOperatorError={byOperatorError}
                onRetryByOperator={() => setByOperatorReload((count) => count + 1)}
                customers={customers}
                customersError={customersError}
                onRetryCustomers={() => setCustomersReload((count) => count + 1)}
              />
            )}
            {tab === "content" && (
              <ContentTab
                key={currentQuestions?.key || "loading"}
                summary={summary}
                previousComplete={previousComplete}
                first={currentQuestions?.first || null}
                error={questionsError}
                onRetry={() => setQuestionsReload((count) => count + 1)}
                chatId={selectedChatId}
                period={period}
                canOpenConversations={canOpenConversations}
              />
            )}
            {tab === "assistant" && (
              <AssistantTab
                summary={summary}
                period={period}
                today={today}
                previousComplete={previousComplete}
                activity={activity}
                activityError={activityError}
                onRetryActivity={() => setActivityReload((count) => count + 1)}
              />
            )}
            {tab === "technical" && <TechnicalSection technical={summary.technical} previousComplete={previousComplete} cut={summary.cut} timeZone={period.tz} capabilities={capabilitiesOf(summary)} />}
            <Freshness freshness={summary.freshness} />
          </div>
        </DownloadPeriodContext.Provider>
      )}
    </div>
  );
};

export default AnalyticsPage;
